//@import dependencies
import React, {useState, useEffect, FocusEvent, useMemo} from 'react'
//@end

//@import components
import AppContainer from 'app_components/layouts/app'
import BreadcrumbComponent from 'app_components/commons/breadcrumb';
import ReactSelectComponent from 'app_components/commons/reactSelect';
import CheckBoxComponent from 'app_components/commons/checkBox/containers/checkBoxComponent';
import InputComponent from 'app_components/commons/Input/InputComponent';
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
//@end

// @import hooks
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next'
// @end

// @INFO Import types
// @end

// @INFO Import services
import CourseSchedulingModeService from 'app_services/admin/courseScheduling/courseSchedulingModeService';
import ReportService from 'app_services/admin/report/reportService';
import { FactoryReportType, ReportFactoryParams } from 'app_services/admin/report/reportService';
import ReactSelectService from 'app_services/components/reactSelectService'
// @end

// @INFO Import Styles
import './../reportsScreen.scss'
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent';

// @ADD Component types
interface IReportsScreenLayoutProps {}

interface OptionSelect{
  label: string;
  value: FactoryReportType;
}

const ReportsScreenLayout: React.FC<IReportsScreenLayoutProps> = (props) => {

  const reactSelectService: ReactSelectService = new ReactSelectService()
  const [listOfCourseModes, setListOfCourseModes] = useState<Array<any>>([]);
  const [loading, updateLoading] = useState<boolean>(false);
  const reportOptions = useMemo((): OptionSelect[] => ([
    { label: 'Por modalidad', value: 'modality' },
    { label: 'Resumen de programas', value: 'overview_programs' },
    { label: 'General de estudiantes', value: 'general_student_certificates' },
    { label: 'Provisión de profesionales', value: 'provision_of_professionals' },
    { label: 'Caracterización de usuarios', value: 'users_characterization' },
    { label: 'Transacciones', value: 'transactions' },
  ]), []);
  const { t } = useTranslation();
  const searchForm = useForm();

  const reportSpy: OptionSelect | undefined = searchForm.watch('report');
  const auditorSpy: boolean = searchForm.watch('auditor');

  // Services
  const courseSchedulingModeService: CourseSchedulingModeService = new CourseSchedulingModeService();
  const reportService = new ReportService();

  useEffect(() => {
    searchForm.register('auditor')
    getCourseModes();
  }, [])

  const getCourseModes = async () => {
    const types: Array<any> = []
    const courseModesResponse = await courseSchedulingModeService.list({moodle_id: true})
    if (courseModesResponse) {
      courseModesResponse.courseSchedulingModes.map((type: any) => {
        types.push({ value: type._id, label: type.name })
      })
    }
    setListOfCourseModes(types)
  }

  const handleOnFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.currentTarget.type = "date";
  }

  const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
      e.currentTarget.type = "text";
  }

  const handleSubmit = (data: Partial<ReportFactoryParams>) => {
    const params = data;
    if (params.modality) {
      params.modality = (params.modality as any).value;
    }
    if (params.report) {
      params.report = (params.report as any).value;
    }
    if (!params?.reportStartDate?.length) {
      delete params.reportStartDate
    }
    if (!params?.reportEndDate?.length) {
      delete params.reportEndDate
    }
    if (typeof params?.serviceIds === 'string') {
      delete params.serviceIds;
    } else if (Array.isArray(params?.serviceIds)) {
      params.serviceIds = reactSelectService.formatReactSelectResponse(params.serviceIds)
    }
    generateReport(params);
  }

  const generateReport = async (params: Partial<ReportFactoryParams>) => {
    updateLoading(true);
    const response = await reportService.generateReport({
      ...params,
      output_format: 'xlsx'
    });
    if (response?.code === 200 && response?.path) {
        window.open(response.path, '_blank', 'noreferrer');
    }
    updateLoading(false);
  }

  return(
    <AppContainer>
      <div className='container-fluid reports_screen-layout'>
        <BreadcrumbComponent links={['#']} texts={[`${t(`components.breadcrumb.reports`)}`]} />
        <h2>Generación de reportes</h2>
        <form
          className="reports_screen--form"
          onSubmit={searchForm.handleSubmit(handleSubmit)}
        >
          <div className="row">
            <div className="col-12 col-md-12">
              <div className="form-group">
                <ReactSelectComponent
                  identifier='report'
                  options={reportOptions}
                  formHooks={searchForm}
                  isMulti={false}
                  required={true}
                  className='reports_screen--form-input'
                  placeholder='Tipo de reporte'
                />
              </div>
            </div>
          </div>
          {reportSpy?.value === 'modality' ?
            <>
              <div className='row'>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <ReactSelectComponent
                      identifier='modality'
                      options={listOfCourseModes}
                      formHooks={searchForm}
                      isMulti={false}
                      required={true}
                      className='reports_screen--form-input'
                      placeholder={t('labels.course_scheduling.schedulingMode')}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <ReactSelectComponent
                      identifier='serviceIds'
                      options={[]}
                      formHooks={searchForm}
                      isMulti={true}
                      required={false}
                      className='reports_screen--form-input'
                      placeholder={t('labels.course_scheduling.serviceIds')}
                      formatCreateLabel={(inputValue?: string) => (`Agregar servicio "${inputValue}"`)}
                      isCreatable
                      components={{
                        DropdownIndicator: null
                      }}
                    />
                  </div>

                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <div className="form-group">
                    <div className='d-flex align-items-center'>
                      <LabelComponent className='mr-2'>
                        Auditor
                      </LabelComponent>
                      <CheckBoxComponent
                        checked={auditorSpy}
                        onClick={() => searchForm.setValue('auditor', !auditorSpy)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          :searchForm.unregister('modality')}
          <div className="row">
            <div className="col-12 col-md-6">
              <div className="form-group">
                <InputComponent
                  type='text'
                  placeholder='Desde'
                  formHook={searchForm.register()}
                  name='reportStartDate'
                  onFocus={handleOnFocus}
                  onBlur={handleOnBlur}
                  className='reports_screen--form-input'
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-gro">
                <InputComponent
                  type='text'
                  placeholder='Hasta'
                  formHook={searchForm.register()}
                  name='reportEndDate'
                  onFocus={handleOnFocus}
                  onBlur={handleOnBlur}
                  className='reports_screen--form-input'
                />
              </div>
            </div>
          </div>

          <ButtonComponent
            type='submit'
            variant='secondary'
            loading={loading}
          >
            Generar
            </ButtonComponent>
        </form>
      </div>
    </AppContainer>
  )
}

export default ReportsScreenLayout
